<script setup lang="ts">
import { computed, ref, toRaw, watchEffect } from 'vue'
import preparedQuestionsHttpService from '@/services/http/preparedQuestionsHttpService.ts'
import dictionariesHttpService from '@/services/http/dictionariesHttpService.ts'
import type {
  PreparedQuestion,
  PreparedQuestionOption,
  Translation,
} from '@/services/http/httpResponses.ts'
import { useToast } from 'primevue/usetoast'
import { type Id, optionsLetters } from '@raildev/shared-utils'
import { Roles } from '@/constants.ts'

const toast = useToast()

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const MIN_OPTIONS_COUNT = 4

type PreparedQuestionOptionForUpdate = {
  id?: Id
  index: number
  translations: Translation[]
}

type PreparedQuestionForUpdate = {
  id?: Id
  allowsMultiple: boolean
  translations: Translation[]
  options: PreparedQuestionOptionForUpdate[]
}

const showDeleted = ref(false)
const editDialogOpen = ref(false)
const preparedQuestionForUpdate = ref<PreparedQuestionForUpdate | null>(null)
const preparedQuestions = ref<PreparedQuestion[]>([])

const refreshQuestions = async () => {
  const preparedQuestionsResponse = await preparedQuestionsHttpService.getPreparedQuestions(
    !showDeleted.value,
  )
  preparedQuestions.value = preparedQuestionsResponse.preparedQuestions
}

watchEffect(refreshQuestions)

const supportedLanguagesResponse = await dictionariesHttpService.getSupportedLanguages()
const languages = supportedLanguagesResponse.languages

const getValue = (translations: Translation[], languageCode: string) => {
  return translations.find((t) => t.languageCode === languageCode)?.value ?? '-'
}

const escapeCsvValue = (value: unknown): string => {
  // todo copy paste from dictionaries/index.vue
  if (value === null || value === undefined) return ''

  let str = String(value)

  if (/[",\n]/.test(str)) {
    str = str.replace(/"/g, '""')
    str = `"${str}"`
  }

  return str
}

const downloadCsvTemplate = () => {
  const idField = 'id'
  const questions = languages.map((x) => escapeCsvValue(`question_${x.code}`))
  const options = languages.flatMap((x) => {
    return optionsLetters.map((l) => escapeCsvValue(`option_${l}_${x.code}`))
  })

  const headers = [idField, ...questions, ...options]

  const rows = preparedQuestions.value.map((x) => {
    const csvRow: Record<string, string> = {}
    csvRow[idField] = x.id

    for (const language of languages) {
      const translationValue = x.translations.find((t) => t.languageCode === language.code)?.value
      csvRow[`question_${escapeCsvValue(language.code)}`] = escapeCsvValue(translationValue)
    }

    for (const optionsLetter of optionsLetters) {
      const optionIndex = optionsLetters.indexOf(optionsLetter) + 1
      const questionOption = x.options.find((o) => o.index == optionIndex)!

      for (const language of languages) {
        const translationValue = questionOption?.translations.find(
          (t) => t.languageCode === language.code,
        )?.value
        csvRow[`option_${optionsLetter}_${language.code}`] = escapeCsvValue(translationValue)
      }
    }
    return csvRow
  })

  const csvBody = rows.map((row) => {
    const csvLine = []

    for (const header of headers) {
      const value = row[header] || ''
      csvLine.push(value)
    }
    return csvLine.join(',')
  })

  const csvContent = headers.join(',') + '\n' + csvBody.join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `questions.csv`)
  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const beforeUploadCsvTemplate = () => {
  toast.add({
    severity: 'info',
    detail: 'Загружается',
    life: 1000,
  })
}

const uploadCsvTemplate = async () => {
  toast.add({
    severity: 'success',
    detail: 'Завершено',
    life: 1000,
  })

  await refreshQuestions()
}

const getSortedVariants = (options: PreparedQuestionOption[]) => {
  const sortedOptions = [...options]
  sortedOptions.sort((a, b) => a.index - b.index)
  return sortedOptions
}

const deletePreparedQuestion = async (id: Id) => {
  if (!confirm('Удалить запись?')) {
    return
  }
  await preparedQuestionsHttpService.deletePreparedQuestion(id)
  await refreshQuestions()
}

const openEditDialog = (preparedQuestion: PreparedQuestion) => {
  const q = structuredClone(toRaw(preparedQuestion))

  const question: PreparedQuestionForUpdate = {
    id: q.id,
    allowsMultiple: q.allowsMultiple,
    translations: q.translations ?? [],
    options: (q.options ?? [])
      .sort((a, b) => a.index - b.index)
      .map((o) => ({
        id: o.id,
        index: o.index,
        translations: o.translations ?? [],
      })),
  }

  preparedQuestionForUpdate.value = question
  editDialogOpen.value = true
}

const openCreateDialog = () => {
  const question: PreparedQuestionForUpdate = {
    allowsMultiple: true,
    translations: [],
    options: optionsLetters.slice(0, MIN_OPTIONS_COUNT).map((_, i) => ({
      index: i + 1,
      translations: [],
    })),
  }

  preparedQuestionForUpdate.value = question
  editDialogOpen.value = true
}

const closeEditDialog = () => {
  preparedQuestionForUpdate.value = null
  editDialogOpen.value = false
}

const normalizeOptionIndexes = () => {
  const value = preparedQuestionForUpdate.value
  if (!value) {
    return
  }
  value.options.sort((a, b) => a.index - b.index)
  value.options.forEach((o, i) => (o.index = i + 1))
}

const addOption = () => {
  const value = preparedQuestionForUpdate.value
  if (!value) {
    return
  }
  if (value.options.length >= optionsLetters.length) {
    return
  }

  normalizeOptionIndexes()

  value.options.push({
    index: value.options.length + 1,
    translations: [],
  })
}

const removeOption = (index: number) => {
  const value = preparedQuestionForUpdate.value
  if (!value) {
    return
  }
  if (value.options.length <= MIN_OPTIONS_COUNT) {
    return
  }

  value.options = value.options.filter((x) => x.index !== index)
  normalizeOptionIndexes()
}

const updateQuestionTranslation = (
  preparedQuestion: PreparedQuestionForUpdate,
  lang: string,
  value: string,
) => {
  const translationValue = preparedQuestion.translations.find((t) => t.languageCode === lang)

  if (translationValue != null) {
    translationValue.value = value
    return
  }

  preparedQuestion.translations.push({ languageCode: lang, value })
}

const updateQuestionOptionTranslation = (
  preparedQuestionOption: PreparedQuestionOptionForUpdate,
  lang: string,
  value: string,
) => {
  const translationValue = preparedQuestionOption.translations.find((t) => t.languageCode === lang)

  if (translationValue != null) {
    translationValue.value = value
    return
  }

  preparedQuestionOption.translations.push({ languageCode: lang, value })
}

const isBlank = (s: string | null | undefined) => (s ?? '').trim().length === 0

const hasAnyNonEmptyTranslation = (translations: Translation[]) => {
  return translations.some((t) => !isBlank(t.value))
}

const updateQuestion = async () => {
  const question = toRaw(preparedQuestionForUpdate.value)

  if (question == null) {
    return
  }

  normalizeOptionIndexes()

  if (!hasAnyNonEmptyTranslation(question.translations)) {
    toast.add({
      severity: 'error',
      detail: 'Заполните название вопроса',
      life: 2500,
    })
    return
  }

  const hasEmptyOptionText = question.options.some(
    (o) => !hasAnyNonEmptyTranslation(o.translations),
  )
  if (hasEmptyOptionText) {
    toast.add({
      severity: 'error',
      detail: 'Заполните варианты ответа',
      life: 3000,
    })
    return
  }

  if (question.id != null) {
    const { id, ...payload } = question
    await preparedQuestionsHttpService.updatePreparedQuestion(id, payload)
  } else {
    const { id: _id, ...payload } = question
    await preparedQuestionsHttpService.createPreparedQuestion(payload)
  }

  await refreshQuestions()
  closeEditDialog()
}

const isUpdate = computed(() => preparedQuestionForUpdate.value?.id != null)

const addOptionDisabled = computed(() => {
  const value = preparedQuestionForUpdate.value
  if (!value) {
    return true
  }
  return value.options.length >= optionsLetters.length
})
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <Toast />

    <DataTable ref="dt" :expanded-rows="[]" :value="preparedQuestions" dataKey="id" class="w-full">
      <template #header>
        <div class="flex flex-wrap gap-2 items-center justify-between">
          <div class="flex flex-row gap-2">
            <h4 class="m-0">Вопросы</h4>
            <Checkbox v-model="showDeleted" input-id="showDeleted" binary />
            <label for="showDeleted">Показывать удаленные</label>
          </div>
          <div class="flex flex-wrap gap-4">
            <Button label="Добавить" icon="pi pi-plus" @click="openCreateDialog()" />
            <div v-if="false">
              <Button label="Скачать данные" icon="pi pi-download" @click="downloadCsvTemplate" />
              <FileUpload
                mode="basic"
                name="csvFile"
                url="/api/v1/admin/prepared-questions"
                accept=".csv"
                auto
                choose-label="Загрузить CSV файл"
                upload-icon="pi pi-upload"
                @beforeUpload="beforeUploadCsvTemplate"
                @upload="uploadCsvTemplate"
                :maxFileSize="1000000"
              />
            </div>
          </div>
        </div>
      </template>
      <Column expander style="width: 5rem" />

      <Column
        v-for="lang in languages"
        :key="lang.code"
        :header="lang.name"
        style="min-width: 12rem"
      >
        <template #body="slotProps">
          {{ getValue(slotProps.data.translations, lang.code) }}
        </template>
      </Column>

      <Column header="Множественный выбор?">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.allowsMultiple ? 'Да' : 'Нет'"
            :severity="slotProps.data.allowsMultiple ? 'success' : 'danger'"
          />
        </template>
      </Column>

      <Column
        headerStyle="width: 5rem; text-align: center"
        bodyStyle="text-align: center; overflow: visible"
      >
        <template #body="slotProps">
          <div class="flex flex-row gap-2">
            <Button
              type="button"
              icon="pi pi-pencil"
              rounded
              @click="openEditDialog(slotProps.data)"
            />
            <Button
              type="button"
              severity="danger"
              icon="pi pi-trash"
              rounded
              @click="deletePreparedQuestion(slotProps.data.id)"
            />
          </div>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="p-4">
          <DataTable :value="getSortedVariants(slotProps.data.options)">
            <template #header>
              <div class="flex flex-wrap gap-2 items-center justify-between">
                <h4 class="m-0">Варианты</h4>
              </div>
            </template>
            <Column
              v-for="lang in languages"
              :key="lang.code"
              :header="lang.name"
              style="min-width: 12rem"
            >
              <template #body="slotProps">
                {{ getValue(slotProps.data.translations, lang.code) }}
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="editDialogOpen"
      class="max-w-[95vw] min-w-[80vw] min-h-[90vh]"
      modal
      :closeOnEscape="false"
      content-class="h-full w-full flex grow"
      :header="isUpdate ? 'Редактировать' : 'Создать'"
    >
      <div class="flex grow flex-col gap-6" v-if="preparedQuestionForUpdate">
        <div class="flex items-center gap-2">
          <Checkbox v-model="preparedQuestionForUpdate.allowsMultiple" input-id="am" binary />
          <label for="am" class="font-medium"
            >Поддерживается множественный выбор вариантов ответа</label
          >
        </div>

        <p class="text-xl font-bold">Вопрос на языках</p>
        <div
          v-for="lang in languages"
          :key="lang.code"
          class="flex flex-col items-center gap-4 w-full"
        >
          <IftaLabel class="w-full">
            <Textarea
              required
              fluid
              :id="`q_${lang.code}`"
              class="flex-auto h-full"
              :style="{ width: 100 / languages.length - 20 }"
              autocomplete="off"
              :model-value="
                preparedQuestionForUpdate.translations.find((t) => t.languageCode == lang.code)
                  ?.value
              "
              @valueChange="
                (x: string) => updateQuestionTranslation(preparedQuestionForUpdate!, lang.code, x)
              "
            />
            <label :for="`q_${lang.code}`">Вариант на {{ lang.name }}</label>
          </IftaLabel>
        </div>
        <div class="flex items-center justify-between">
          <p class="text-xl font-bold m-0">Варианты ответа</p>
          <Button
            label="Добавить вариант"
            icon="pi pi-plus"
            @click="addOption()"
            :disabled="addOptionDisabled"
          />
        </div>
        <div class="flex flex-col gap-4">
          <div
            v-for="option in preparedQuestionForUpdate.options"
            :key="option.index"
            class="border border-gray-300 rounded-lg p-4 relative"
          >
            <div class="flex items-center gap-2 mb-3">
              <span class="font-bold text-lg">
                {{ optionsLetters[option.index - 1] ?? option.index }}
              </span>
              <div class="grow"></div>
              <Button
                type="button"
                severity="danger"
                icon="pi pi-trash"
                rounded
                :disabled="preparedQuestionForUpdate.options.length <= MIN_OPTIONS_COUNT"
                @click="removeOption(option.index)"
              />
            </div>
            <div class="flex flex-wrap gap-4">
              <div v-for="lang in languages" :key="lang.code" class="flex-1 min-w-[220px]">
                <IftaLabel class="w-full">
                  <Textarea
                    required
                    fluid
                    :id="`qo_${option.index}_${lang.code}`"
                    class="flex-auto h-full"
                    :model-value="
                      option.translations.find((t) => t.languageCode == lang.code)?.value
                    "
                    @valueChange="
                      (x: string) => updateQuestionOptionTranslation(option, lang.code, x)
                    "
                  />
                  <label :for="`qo_${option.index}_${lang.code}`">{{ lang.name }}</label>
                </IftaLabel>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex items-center w-full gap-2 pt-4">
          <Button
            type="button"
            label="Отменить"
            severity="secondary"
            @click="closeEditDialog()"
          ></Button>

          <div class="grow"></div>

          <Button
            type="button"
            :label="isUpdate ? 'Обновить' : 'Добавить'"
            @click="updateQuestion()"
          ></Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped></style>
